import React from 'react';
import { Link } from '@inertiajs/react';
import { StatCard } from '../components/StatCard';
import { BenefitCard } from '../components/BenefitCard';
import { Users, Heart, Briefcase, Calendar } from 'lucide-react';

const CommunityPage: React.FC = () => {
  return (
    <div className='animate-fade-in'>
      <section className='py-16 md:py-20 text-center bg-muted'>
        <div className='container mx-auto px-6'>
          <h1 className='text-4xl md:text-5xl font-bold text-foreground font-heading'>
            Our <span className='text-primary'>Community</span>
          </h1>
          <p className='text-lg text-muted-foreground mt-4 max-w-3xl mx-auto'>
            Where learning continues, resources are shared, and peers support one another.
          </p>
        </div>
      </section>

      {/* Image Collage Section */}
      <section className='py-12'>
        <div className='container mx-auto px-6'>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <div className='overflow-hidden rounded-lg shadow-lg md:col-span-2'>
              <img
                src='https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2070&auto=format&fit=crop'
                alt='Community members collaborating'
                className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-500'
              />
            </div>
            <div className='overflow-hidden rounded-lg shadow-lg'>
              <img
                src='https://images.unsplash.com/photo-1573497491208-6b1acb260507?q=80&w=2070&auto=format&fit=crop'
                alt='Workshop presentation'
                className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-500'
              />
            </div>
            <div className='overflow-hidden rounded-lg shadow-lg'>
              <img
                src='https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=2132&auto=format&fit=crop'
                alt='Team discussion'
                className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-500'
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className='py-20 bg-muted'>
        <div className='container mx-auto px-6'>
          <div className='text-center max-w-3xl mx-auto mb-12'>
            <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3'>Better Together</p>
            <h2 className='text-3xl md:text-4xl font-bold text-foreground font-heading'>
              Why Join Our Thriving Community?
            </h2>
            <p className='text-lg text-muted-foreground mt-4'>
              At DigitalWave, we believe that community is the cornerstone of effective learning and career growth.
            </p>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
            <BenefitCard
              accent='primary'
              icon={<Users className='h-8 w-8' />}
              title='Peer-to-Peer Support'
              description='Get help with coding problems, share project ideas, and collaborate with fellow learners.'
            />
            <BenefitCard
              accent='secondary'
              icon={<Heart className='h-8 w-8' />}
              title='Exclusive Resources'
              description='Access a curated library of articles, tutorials, and career resources shared by the community.'
            />
            <BenefitCard
              accent='orange'
              icon={<Briefcase className='h-8 w-8' />}
              title='Networking Events'
              description='Connect with peers, mentors, and industry professionals to expand your professional network.'
            />
            <BenefitCard
              accent='primary'
              icon={<Calendar className='h-8 w-8' />}
              title='Workshops & Talks'
              description='Participate in exclusive workshops and tech talks to stay ahead of the curve.'
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className='py-20'>
        <div className='container mx-auto px-6'>
          <div className='text-center max-w-3xl mx-auto mb-12'>
            <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3'>Our Growth</p>
            <h2 className='text-3xl md:text-4xl font-bold text-foreground font-heading'>
              Our Community By The Numbers
            </h2>
          </div>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto'>
            <StatCard accent='primary' value='500+' label='Members' />
            <StatCard accent='secondary' value='15+' label='Workshops Held' />
            <StatCard accent='primary' value='20+' label='Mentors' />
            <StatCard accent='secondary' value='100+' label='Projects Built' />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className='relative bg-cover bg-center'
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop')",
        }}
      >
        <div className='absolute inset-0 bg-primary/80 backdrop-blur-sm'></div>
        <div className='container mx-auto px-6 py-20 relative z-10 text-center'>
          <h2 className='text-3xl md:text-4xl font-bold text-primary-foreground mb-4 font-heading'>
            Ready to Start Your Journey?
          </h2>
          <p className='max-w-2xl mx-auto mb-8 text-primary-foreground/90'>
            Become part of a movement that's shaping the future of tech in our community. Your next chapter starts here.
          </p>
          <Link
            href='#'
            className='inline-block bg-background text-foreground font-bold px-10 py-4 rounded-full text-lg hover:bg-background/90 transition-all duration-300 transform hover:scale-105 shadow-lg'
          >
            Join our Discord Server
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CommunityPage;
